// frontend/src/components/AnnouncementsListModal.js

import React from "react";
import { PencilSimple, Trash, X, Check, PushPin } from "phosphor-react";
import AnnouncementCreateModal from "./AnnouncementCreateModal";

export default function AnnouncementsListModal({
  open,
  onClose,
  announcements = [],
  isAdmin = false,
  groupId,
  onCreated,
  onEdit,
  onDelete,
}) {
  const [editingId, setEditingId] = React.useState(null);
  const [deletingId, setDeletingId] = React.useState(null);
  const [savingId, setSavingId] = React.useState(null);
  const [drafts, setDrafts] = React.useState({});
  const [creating, setCreating] = React.useState(false);
  const [deleteConfirm, setDeleteConfirm] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;

    const init = {};
    announcements.forEach((a) => {
      const { title, body } = parseTitleBody(a?.body || "");
      init[a.id] = {
        title,
        body,
        pinned: !!a?.pinned,
      };
    });

    setDrafts(init);
    setEditingId(null);
    setDeletingId(null);
    setSavingId(null);
    setCreating(false);
    setDeleteConfirm(null);
  }, [open, announcements]);

  if (!open) return null;

  if (creating) {
    return (
      <AnnouncementCreateModal
        groupId={groupId}
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={async () => {
          await onCreated?.();
          setCreating(false);
        }}
      />
    );
  }

  const handleDelete = async (id) => {
    if (!onDelete) return;

    try {
      setDeletingId(id);
      await onDelete(id);
    } finally {
      setDeletingId(null);
      setDeleteConfirm(null);
    }
  };

  const handleStartEdit = (a) => {
    setEditingId(a.id);

    setDrafts((prev) => {
      if (prev[a.id]) return prev;

      const { title, body } = parseTitleBody(a?.body || "");
      return { ...prev, [a.id]: { title, body, pinned: !!a?.pinned } };
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSave = async (a) => {
    if (!onEdit) return;

    const d = drafts[a.id] || { title: "", body: "", pinned: false };
    const title = (d.title || "").trim();
    const body = (d.body || "").trim();

    if (!title && !body) {
      alert("Please enter a title or message.");
      return;
    }

    try {
      setSavingId(a.id);
      await onEdit(a.id, { title, body, pinned: !!d.pinned });
      setEditingId(null);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] grid place-items-center bg-black/35 overflow-y-auto p-0 md:p-6">
      <div
        className="
          relative z-10 w-full max-w-2xl overflow-hidden
          rounded-[28px] border border-blue-100/80
          bg-white text-blue-950
          p-4 md:p-6
          shadow-[0_8px_0px_rgba(37,99,235,0.96)]
        "
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcements-title"
      >
        <button
          onClick={onClose}
          className="
            absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center
            rounded-full text-blue-500 
            transition hover:bg-blue-200
          "
          aria-label="Close"
        >
          <X size={19} weight="bold" />
        </button>

        <div className="text-center">
          <h3
            id="announcements-title"
            className="text-blue-600 font-black leading-none tracking-[-0.04em] text-[22px] sm:text-[22px]"
          >
            Announcements
          </h3>
        </div>

        <div className="mt-5 rounded-[22px] bg-blue-50 p-3 md:p-4 shadow-[0_10px_30px_rgba(37,99,235,0.06)]">
          <div className="max-h-[70dvh] overflow-y-auto rounded-[18px]">
            {announcements.length === 0 ? (
              <div className="rounded-2xl border border-blue-100 bg-white/90 px-3 py-4 text-sm font-semibold text-blue-500">
                No announcements
              </div>
            ) : (
              <div className="space-y-2">
                {announcements.map((a) => {
                  const isEditing = editingId === a.id;
                  const isDeleting = deletingId === a.id;
                  const isSaving = savingId === a.id;

                  const draft = drafts[a.id] || {
                    title: "",
                    body: "",
                    pinned: !!a?.pinned,
                  };

                  const { title, body } = isEditing
                    ? draft
                    : parseTitleBody(a?.body || "");

                  return (
                    <div
                      key={a.id}
                      className="
                        rounded-2xl border border-blue-100/70 bg-white/90
                        px-4 py-2 shadow-sm transition
                      "
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 flex items-center min-h-[36px]">
                          {!isEditing ? (
                            <div className="w-full">
                              <div className="flex items-start gap-1.5 text-sm font-bold text-blue-900 break-words">
                                {a?.pinned ? (
                                  <PushPin
                                    size={15}
                                    weight="fill"
                                    className="mt-[2px] shrink-0 text-blue-500"
                                  />
                                ) : null}

                                <span>{title || "(No title)"}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full space-y-2">
                              <input
                                type="text"
                                className="
                                  w-full border border-blue-100 rounded-2xl px-3 py-2
                                  text-sm font-medium text-black
                                  focus:outline-none focus:ring-2 focus:ring-blue-600
                                "
                                placeholder="Title"
                                value={draft.title}
                                onChange={(e) =>
                                  setDrafts((prev) => ({
                                    ...prev,
                                    [a.id]: {
                                      ...prev[a.id],
                                      title: e.target.value,
                                    },
                                  }))
                                }
                              />

                              <textarea
                                rows={4}
                                className="
                                  w-full border border-blue-100 rounded-2xl px-3 py-2
                                  text-sm text-black
                                  focus:outline-none focus:ring-2 focus:ring-blue-600
                                "
                                placeholder="Message"
                                value={draft.body}
                                onChange={(e) =>
                                  setDrafts((prev) => ({
                                    ...prev,
                                    [a.id]: {
                                      ...prev[a.id],
                                      body: e.target.value,
                                    },
                                  }))
                                }
                              />

                              <label className="inline-flex cursor-pointer items-center gap-2 ml-2 text-sm font-medium text-blue-700 select-none">
                                <input
                                  type="checkbox"
                                  checked={!!draft.pinned}
                                  onChange={(e) =>
                                    setDrafts((prev) => ({
                                      ...prev,
                                      [a.id]: {
                                        ...prev[a.id],
                                        pinned: e.target.checked,
                                      },
                                    }))
                                  }
                                  className="peer sr-only"
                                />

                                <div className="flex h-4 w-4 items-center justify-center rounded-3xl bg-blue-50 border border-slate-200">
                                  <Check
                                    size={12}
                                    weight="bold"
                                    className={`text-blue-500 transition-opacity ${
                                      draft.pinned ? "opacity-100" : "opacity-0"
                                    }`}
                                  />
                                </div>

                                <span>Pin this announcement</span>
                              </label>
                            </div>
                          )}
                        </div>

                        {isAdmin && (
                          <div className="flex shrink-0 items-center gap-1.5">
                            {!isEditing ? (
                              <>
                                {typeof onEdit === "function" && (
                                  <button
                                    type="button"
                                    onClick={() => handleStartEdit(a)}
                                    className="
                                      inline-flex items-center gap-1 rounded-full
                                      border border-blue-100 bg-white px-2.5 py-1.5
                                      text-xs font-bold text-blue-500 shadow-sm
                                      transition hover:bg-blue-50
                                    "
                                    title="Edit"
                                  >
                                    <PencilSimple size={14} />
                                    Edit
                                  </button>
                                )}

                                {typeof onDelete === "function" && (
                                  <button
                                    type="button"
                                    onClick={() => setDeleteConfirm(a.id)}
                                    disabled={isDeleting}
                                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-bold transition ${
                                      isDeleting
                                        ? "bg-red-100 text-red-700 opacity-75 cursor-not-allowed"
                                        : "bg-red-50 text-red-700 hover:bg-red-100"
                                    }`}
                                    title="Delete"
                                  >
                                    <Trash size={14} />
                                    {isDeleting ? "Deleting…" : "Delete"}
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  className="
                                    inline-flex items-center gap-1 rounded-full
                                    border border-blue-100 bg-white px-2.5 py-1.5
                                    text-xs font-bold text-blue-500 shadow-sm
                                    transition hover:bg-blue-50
                                  "
                                  disabled={isSaving}
                                  title="Cancel"
                                >
                                  Cancel
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleSave(a)}
                                  className="
                                    inline-flex items-center gap-1 rounded-full
                                    bg-blue-600 px-2.5 py-1.5
                                    text-xs font-bold text-white
                                    transition hover:bg-blue-700 disabled:opacity-50
                                  "
                                  disabled={isSaving}
                                  title="Save"
                                >
                                  {isSaving ? "Saving…" : "Save"}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {!isEditing && body && (
                        <div className="text-sm leading-5 text-blue-900/75 whitespace-pre-wrap break-words">
                          {body}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {isAdmin && groupId && (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="
                  inline-flex h-8 items-center justify-center rounded-full
                  border border-blue-100 bg-blue-700 px-3
                  text-sm font-semibold text-white
                  transition hover:bg-blue-400
                "
              >
                Create
              </button>
            </div>
          )}
        </div>
      </div>

      {deleteConfirm && (
        <div
          aria-live="polite"
          aria-atomic="true"
          className="instrument-font pointer-events-none fixed bottom-6 inset-x-0 z-[200] flex justify-center px-4"
        >
          <div
            role="alert"
            className="
              pointer-events-auto relative w-full max-w-md
              rounded-full bg-blue-200 px-10 py-4
              shadow-[0_8px_0px_rgba(37,99,235,0.96)] backdrop-blur-md
              transition-all ease-out
            "
          >
            <div className="pr-0">
              <div className="flex items-center gap-1">
                <Trash
                  className="shrink-0 text-red-600"
                  size={16}
                  weight="fill"
                />

                <p className="text-sm  text-gray-900">
                  Delete this announcement?
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="rounded-full border border-blue-100 bg-white px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deletingId === deleteConfirm}
                  className="rounded-full bg-red-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-600 disabled:opacity-50"
                >
                  {deletingId === deleteConfirm ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function parseTitleBody(full) {
  const text = String(full || "").replace(/\r\n/g, "\n").trim();
  if (!text) return { title: "", body: "" };

  const idx = text.indexOf("\n");
  if (idx === -1) return { title: text, body: "" };

  const title = text.slice(0, idx).trim();
  const body = text.slice(idx + 1).trim();

  return { title, body };
}